import { FC, useState } from 'react'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  Divider,
  Grid2,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Typography,
  alpha,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { useNavigate } from 'react-router-dom'
import {
  MdArrowForward,
  MdCheck,
  MdExpandMore,
  MdLightbulbOutline,
  MdOutlineBrush,
  MdOutlineGridView,
  MdOutlineMenuBook,
  MdOutlinePeopleAlt,
  MdOutlineSettings,
} from 'react-icons/md'

// ─── Tipos ─────────────────────────────────────────────────────────────────────

interface IGlossaryItem {
  term: string
  definition: string
}

interface IManualCard {
  title: string
  subtitle: string
  description: string
  icon: React.ReactNode
  color: string
  path: string
  steps: string[]
  glossary: IGlossaryItem[]
  tip?: string
}

// ─── Conteúdo dos Cards ────────────────────────────────────────────────────────

const CARDS: IManualCard[] = [
  {
    title: 'Arte Final',
    subtitle: 'Fluxo 1',
    description:
      'Quadro Kanban para acompanhar o andamento das artes de personalização dos produtos pedidos pelos clientes. Cada card representa um produto de um pedido que precisa passar pelo processo de criação antes de ir para produção.',
    icon: <MdOutlineBrush size={40} />,
    color: '#2e7d32',
    path: '/hcp/arte-andamento',
    steps: [
      'Acesse pelo menu lateral em "Fluxos → Arte Final".',
      'Os cards aparecem agrupados por coluna conforme o status atual da arte.',
      'Use os filtros no topo da tela para encontrar artes por designer, etiqueta, produto ou prazo.',
      'Clique em um card para abrir os detalhes do produto e ver o histórico de movimentações.',
      'Para avançar uma arte de etapa, use o botão de ação dentro do card ou arraste entre colunas.',
      'A tela se atualiza automaticamente a cada 5 minutos — nenhuma ação é necessária para ver o estado mais recente.',
    ],
    glossary: [
      { term: 'Não Iniciada', definition: 'Arte solicitada mas nenhum designer começou a trabalhar nela ainda.' },
      { term: 'Em Andamento', definition: 'Um designer está atualmente criando ou ajustando a arte.' },
      { term: 'Revisão do Cliente', definition: 'Arte enviada para o cliente avaliar e aprovar ou solicitar alterações.' },
      { term: 'Alteração em Andamento', definition: 'Cliente pediu mudanças e o designer está refazendo a arte.' },
      { term: 'Aprovado Final', definition: 'Arte aprovada pelo cliente. Pronta para seguir para a produção física.' },
      { term: 'Designer', definition: 'Profissional responsável por criar a arte. Cada card pode estar atribuído a um designer.' },
      { term: 'Etiqueta', definition: 'Marcação colorida que você coloca no card para identificar prioridade ou tipo (ex: Urgente, VIP).' },
      { term: 'Thumbnail', definition: 'Miniatura da arte criada, exibida dentro do card para referência visual rápida.' },
    ],
    tip: 'Filtre por "Prazo" para priorizar as artes com entrega mais próxima e evitar atrasos.',
  },
  {
    title: 'Montagem de Cartelas',
    subtitle: 'Fluxo 2',
    description:
      'Tela para organizar os produtos com arte aprovada em cartelas de produção. Uma cartela é uma folha onde vários produtos são impressos juntos para corte posterior — montar lotes maiores reduz o custo por unidade e otimiza o tempo de produção.',
    icon: <MdOutlineGridView size={40} />,
    color: '#1565c0',
    path: '/hcp/montagem-cartelas',
    steps: [
      'Produtos que passaram pela coluna "Aprovado Final" no Fluxo 1 aparecem automaticamente aqui.',
      'Selecione os produtos que serão montados no mesmo lote de produção.',
      'O sistema calcula quantas cartelas são necessárias com base na tiragem mínima configurada.',
      'Revise a composição do lote e confirme para enviar para a produção física.',
      'Lotes confirmados ficam registrados com data e responsável para rastreabilidade.',
    ],
    glossary: [
      { term: 'Cartela', definition: 'Folha com múltiplos produtos impressos lado a lado, cortados individualmente depois.' },
      { term: 'Tiragem mínima', definition: 'Quantidade mínima de unidades que precisam ser produzidas de uma vez para compensar o custo de setup. Configurada na tela de Configurações.' },
      { term: 'Lote', definition: 'Conjunto de produtos selecionados para produção conjunta em uma ou mais cartelas.' },
      { term: 'Aprovados sem OS', definition: 'Produtos com arte aprovada que ainda não foram incluídos em nenhum lote de produção.' },
    ],
    tip: 'Agrupe produtos do mesmo tipo (ex: mesmo tamanho ou material) para aproveitar melhor o espaço de cada cartela.',
  },
  {
    title: 'Configurações',
    subtitle: 'Gestão de Prazos',
    description:
      'Central de gerenciamento dos prazos de entrega que aparecem no site do Frete Hero. O prazo que o cliente vê na hora de comprar é definido aqui — cada produto pode ter um prazo diferente, e você distribui esse prazo entre as etapas internas de produção.',
    icon: <MdOutlineSettings size={40} />,
    color: '#e65100',
    path: '/hcp/configuracoes',
    steps: [
      'A lista exibe todos os produtos cadastrados com seus prazos atuais no Frete Hero.',
      'Clique em um produto para editar o prazo total e a distribuição entre os fluxos internos.',
      'Para alterar vários produtos de uma vez, use a edição em massa: selecione os produtos e aplique o novo prazo.',
      'Salve as alterações — elas entram na fila de "Pendentes" até serem sincronizadas com o site.',
      'Acompanhe a aba "Pendentes" para saber quais alterações ainda não foram enviadas ao Frete Hero.',
      'A sincronização acontece automaticamente; você também pode forçar manualmente pelo botão "Sincronizar agora".',
    ],
    glossary: [
      { term: 'Prazo total', definition: 'Quantidade de dias úteis que o cliente vê no site para receber o pedido após a compra.' },
      { term: 'Fluxo interno', definition: 'Divisão do prazo total entre as etapas da produção: Arte, Montagem, Produção e Logística. Serve para que cada equipe saiba quanto tempo tem.' },
      { term: 'Alteração pendente', definition: 'Mudança de prazo salva no sistema mas ainda não enviada (sincronizada) com o site do Frete Hero.' },
      { term: 'Sincronização', definition: 'Processo de envio das alterações salvas para o Frete Hero, fazendo o site exibir os novos prazos para os clientes.' },
      { term: 'Edição em massa', definition: 'Recurso para aplicar o mesmo prazo a vários produtos ao mesmo tempo, sem precisar editar um por um.' },
    ],
    tip: 'Sempre confira a aba "Pendentes" antes de encerrar o dia — alterações não sincronizadas não aparecem para o cliente.',
  },
  {
    title: 'Colaboradores',
    subtitle: 'Gestão de Equipe',
    description:
      'Gerenciamento dos usuários com acesso ao HCP. Cada membro da equipe tem um login próprio e permissões específicas — assim cada pessoa vê somente as telas do seu trabalho, sem risco de acesso indevido a outras áreas.',
    icon: <MdOutlinePeopleAlt size={40} />,
    color: '#6a1b9a',
    path: '/hcp/colaboradores',
    steps: [
      'Clique em "Novo Colaborador" para adicionar um membro da equipe.',
      'Preencha nome, e-mail e defina uma senha inicial para o primeiro acesso.',
      'Selecione as permissões de acordo com a função: marque apenas o que a pessoa precisa usar.',
      'Salve — o colaborador já pode entrar com o e-mail e senha cadastrados.',
      'Para editar um colaborador existente, clique no nome dele na lista e altere as informações ou permissões.',
      'Para remover o acesso de alguém, desative ou exclua o cadastro.',
    ],
    glossary: [
      { term: 'Permissão: Arte Final', definition: 'Libera acesso à tela do Fluxo 1 — acompanhamento e movimentação de artes no Kanban.' },
      { term: 'Permissão: Montagem', definition: 'Libera acesso à tela do Fluxo 2 — montagem e confirmação de lotes de cartelas.' },
      { term: 'Permissão: Configurações', definition: 'Libera acesso ao gerenciamento de prazos do Frete Hero. Recomendado apenas para gestores.' },
      { term: 'Permissão: Colaboradores', definition: 'Libera acesso a esta tela de gestão da equipe. Equivale a um papel de administrador do HCP.' },
      { term: 'Senha inicial', definition: 'Senha que você define para o colaborador no cadastro. Recomende que ele troque no primeiro acesso.' },
    ],
    tip: 'Dê permissão de "Colaboradores" somente para quem realmente precisa gerenciar a equipe — é o nível mais alto de acesso no HCP.',
  },
]

// ─── Componente de Card ────────────────────────────────────────────────────────

const ManualCard: FC<IManualCard> = ({ title, subtitle, description, icon, color, path, steps, glossary, tip }) => {
  const navigate = useNavigate()
  const [expanded, setExpanded] = useState<string | false>('steps')

  const handleChange = (panel: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false)
  }

  const accordionSx = {
    boxShadow: 'none',
    '&:before': { display: 'none' },
    '&.Mui-expanded': { margin: 0 },
    borderTop: '1px solid rgba(0,0,0,0.06)',
  }

  return (
    <Paper
      elevation={0}
      sx={{
        border: '1px solid rgba(0,0,0,0.06)',
        borderRadius: '20px',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        transition: 'box-shadow 0.25s ease',
        '&:hover': { boxShadow: `0 6px 24px ${alpha(color, 0.12)}` },
      }}
    >
      {/* ── Header colorido ── */}
      <Box
        sx={{
          p: 3,
          background: `linear-gradient(135deg, ${color} 0%, ${alpha(color, 0.75)} 100%)`,
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <Box sx={{ opacity: 0.95, flexShrink: 0 }}>{icon}</Box>
        <Box>
          <Typography variant="caption" sx={{ opacity: 0.8, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, display: 'block' }}>
            {subtitle}
          </Typography>
          <Typography variant="h6" fontWeight={900} sx={{ lineHeight: 1.15, letterSpacing: -0.5 }}>
            {title}
          </Typography>
        </Box>
      </Box>

      {/* ── Descrição ── */}
      <Box sx={{ px: 3, py: 2.5 }}>
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.65 }}>
          {description}
        </Typography>
      </Box>

      {/* ── Passo a passo ── */}
      <Accordion
        expanded={expanded === 'steps'}
        onChange={handleChange('steps')}
        disableGutters
        sx={accordionSx}
      >
        <AccordionSummary expandIcon={<MdExpandMore />} sx={{ px: 3, minHeight: 48 }}>
          <Typography variant="subtitle2" fontWeight={800} color="text.primary">
            Como usar — passo a passo
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 3, pt: 0, pb: 2.5 }}>
          <List dense disablePadding>
            {steps.map((step, i) => (
              <ListItem key={i} disablePadding alignItems="flex-start" sx={{ mb: 1 }}>
                <ListItemIcon sx={{ minWidth: 30, mt: 0.3 }}>
                  <Box
                    sx={{
                      width: 20,
                      height: 20,
                      borderRadius: '50%',
                      bgcolor: alpha(color, 0.12),
                      color,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.65rem',
                      fontWeight: 900,
                      flexShrink: 0,
                    }}
                  >
                    {i + 1}
                  </Box>
                </ListItemIcon>
                <ListItemText
                  primary={step}
                  primaryTypographyProps={{ variant: 'body2', color: 'text.secondary', lineHeight: 1.55 }}
                />
              </ListItem>
            ))}
          </List>
        </AccordionDetails>
      </Accordion>

      {/* ── Glossário ── */}
      <Accordion
        expanded={expanded === 'glossary'}
        onChange={handleChange('glossary')}
        disableGutters
        sx={accordionSx}
      >
        <AccordionSummary expandIcon={<MdExpandMore />} sx={{ px: 3, minHeight: 48 }}>
          <Typography variant="subtitle2" fontWeight={800} color="text.primary">
            O que significa cada coisa?
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 3, pt: 0, pb: 2.5 }}>
          {glossary.map((item, i) => (
            <Box key={i} sx={{ mb: i < glossary.length - 1 ? 1.5 : 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <MdCheck size={14} color={color} style={{ marginTop: 3, flexShrink: 0 }} />
                <Box>
                  <Typography component="span" variant="body2" fontWeight={800} color="text.primary">
                    {item.term}
                  </Typography>
                  <Typography component="span" variant="body2" color="text.secondary">
                    {' — '}{item.definition}
                  </Typography>
                </Box>
              </Box>
            </Box>
          ))}
        </AccordionDetails>
      </Accordion>

      {/* ── Dica ── */}
      {tip && (
        <Box sx={{ px: 3, py: 2, bgcolor: alpha(color, 0.04), borderTop: '1px solid rgba(0,0,0,0.06)' }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <MdLightbulbOutline size={16} color={color} style={{ marginTop: 2, flexShrink: 0 }} />
            <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.5 }}>
              <Typography component="span" variant="caption" fontWeight={800} sx={{ color }}>Dica: </Typography>
              {tip}
            </Typography>
          </Box>
        </Box>
      )}

      <Divider />

      {/* ── Footer ── */}
      <Box sx={{ p: 2 }}>
        <Button
          variant="outlined"
          fullWidth
          endIcon={<MdArrowForward />}
          onClick={() => navigate(path)}
          sx={{
            borderColor: color,
            color,
            borderRadius: '10px',
            fontWeight: 700,
            '&:hover': { borderColor: color, bgcolor: alpha(color, 0.06) },
          }}
        >
          Ir para a tela
        </Button>
      </Box>
    </Paper>
  )
}

// ─── Página Principal ──────────────────────────────────────────────────────────

const ManualUsuario: FC = () => {
  const theme = useTheme()

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1200, margin: '0 auto' }}>
      {/* Header */}
      <Box sx={{ mb: 5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
          <Box
            sx={{
              p: 1.5,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              color: theme.palette.primary.main,
              display: 'flex',
            }}
          >
            <MdOutlineMenuBook size={28} />
          </Box>
          <Chip
            label="Guia Rápido"
            size="small"
            sx={{
              bgcolor: alpha('#2e7d32', 0.1),
              color: '#2e7d32',
              fontWeight: 800,
              fontSize: '0.7rem',
              letterSpacing: 0.5,
            }}
          />
        </Box>

        <Typography variant="h4" fontWeight={900} sx={{ letterSpacing: -1, mb: 0.5 }}>
          Manual do Usuário
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 600 }}>
          Entenda o que cada tela do HCP faz, como usar passo a passo e o que significa cada termo.
          Clique nas seções dentro de cada card para expandir o conteúdo.
        </Typography>
      </Box>

      {/* Grid de Cards */}
      <Grid2 container spacing={3}>
        {CARDS.map((card) => (
          <Grid2 key={card.path} size={{ xs: 12, md: 6 }}>
            <ManualCard {...card} />
          </Grid2>
        ))}
      </Grid2>
    </Box>
  )
}

export default ManualUsuario
